import { useEffect, useState } from 'react';
import { getFormActionButtonsWithInvoiceParams, getFormTitle, transformFormData } from '../../utils';
import type { PhieuNhapKho, PhieuNhapKhoInput } from '@/types/schemas';
import { handleUpdateRowFields } from '../../utils/calc-util';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import ConfirmDialog from '../../components/ConfirmDialog';
import { usePhieuNhapKhoContext } from '../../context';
import { AritoFormV2 } from '@/components/custom/erp';
import { useAuth } from '@/contexts/auth-context';
import { initialFormValues } from '../../schemas';
import type { FormSchema } from '../../schemas';
import { useFormFieldState } from '../../hooks';
import { BasicInfoTab } from './BasicInfoTab';
import InfoDropdown from './InfoDropdown';
import { FormMode } from '@/types/form';
import { DetailTab } from './DetailTab';
import { useDetailRows } from './hooks';
import { OtherTab } from './OtherTab';
import BottomBar from './BottomBar';

interface FormDialogProps {
  formMode: FormMode;
  isCopyMode?: boolean;
  open?: boolean;
  initialData?: Partial<PhieuNhapKho> | null;
  onSubmit?: (data: PhieuNhapKhoInput) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
  fromInvoice?: { nguon_hoa_don_id: string };
}

const FormDialog = ({
  open,
  formMode,
  isCopyMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const { entityUnit } = useAuth();
  const { hasInvoiceParams, transactionOptions } = usePhieuNhapKhoContext();
  const {
    rows: detailRows,
    setRows: setDetailRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange,
    setNonWritableFields: detailSetNonWritableFields,
    setPasteConfigs: detailSetPasteConfigs
  } = useDetailRows(initialData?.chi_tiet_data || [], undefined, handleUpdateRowFields, formMode === 'add');
  const { state, actions } = useFormFieldState(initialData);
  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows
  });

  const normalizeSubmitData = (data: FormSchema): FormSchema => {
    const defaultMaGD =
      formMode === 'add'
        ? transactionOptions[0]?.value || initialFormValues.ma_gd || 'NB'
        : initialData?.ma_gd ||
          initialData?.ma_gd_data?.uuid ||
          transactionOptions[0]?.value ||
          initialFormValues.ma_gd ||
          'NB';

    return {
      ...initialFormValues,
      ...data,
      ma_gd: data.ma_gd || defaultMaGD
    };
  };

  const handleSubmit = (data: FormSchema) => {
    const submitData = normalizeSubmitData(data);
    const formData = transformFormData(submitData, state, detailRows, entityUnit, formMode === 'edit');

    const nguon_hoa_don_id = isCopyMode ? undefined : initialData?.nguon_hoa_don_id;
    const tu_dong_tao = isCopyMode ? undefined : initialData?.tu_dong_tao;

    onSubmit?.({ ...formData, nguon_hoa_don_id, tu_dong_tao });
  };

  const title = getFormTitle(formMode, 'info');
  const actionButtons = getFormActionButtonsWithInvoiceParams(hasInvoiceParams, formMode, 'info', {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    onClose
  });

  return (
    <>
      <AritoFormV2
        mode={formMode}
        initialData={(initialData || initialFormValues) as Partial<FormSchema>}
        title={title}
        actionButtons={actionButtons}
        subTitle='Phiếu nhập kho'
        className='h-[100dvh] max-h-[100dvh] overflow-hidden'
        onSubmit={handleSubmit}
        onClose={onClose}
        headerFields={[
          {
            id: 'info',
            label: 'Thông tin',
            component: (
              <BasicInfoTab
                formMode={formMode}
                customer={state.khachHang}
                currentTransaction={initialData?.ma_gd_data}
                soChungTu={state.soChungTu}
                setCustomer={actions.setKhachHang}
                setQuyenChungTu={actions.setQuyenChungTu}
                setSoChungTu={actions.setSoChungTu}
              />
            )
          }
        ]}
        headerTabActions={
          formMode !== 'add' && initialData?.uuid ? (
            <InfoDropdown uuid={initialData.uuid} ma_ct={initialData?.chung_tu ?? undefined} />
          ) : undefined
        }
        tabs={[
          {
            id: 'details',
            label: 'Chi tiết',
            component: (
              <DetailTab
                formMode={formMode}
                rows={detailRows}
                documentNumber={state.soChungTu || initialData?.so_ct}
                selectedRowUuid={detailSelectedRowUuid}
                selectedCell={detailSelectedCell}
                onRowClick={detailHandleRowClick}
                onCellClick={detailHandleCellClick}
                onAddRow={detailHandleAddRow}
                onDeleteRow={detailHandleDeleteRow}
                onCopyRow={detailHandleCopyRow}
                onPasteRow={detailHandlePasteRow}
                onMoveRow={detailHandleMoveRow}
                onCellValueChange={(rowUuid, field, newValue) => {
                  if (!rowUuid) return;
                  detailHandleCellValueChange(
                    rowUuid,
                    field,
                    newValue as Parameters<typeof detailHandleCellValueChange>[2]
                  );
                }}
                handleUpdateNonWritableFields={detailSetNonWritableFields}
                handleUpdatePasteConfigs={detailSetPasteConfigs}
                onRepeatForRows={handleRepeatDetailRows}
              />
            )
          },
          {
            id: 'other',
            label: 'Khác',
            component: <OtherTab formMode={formMode} formState={{ state, actions }} />
          }
        ]}
        bottomBar={<BottomBar detailRows={detailRows} state={state} />}
      />
    </>
  );
};

export default FormDialog;
